import { PreviewCard as BasePreviewCard } from '@base-ui/react/preview-card';
import type { ComponentProps, ReactElement, ReactNode } from 'react';
import { cx } from '../lib/styled.tsx';
import { popupArrow, popupSurface, popupTransition } from '../lib/surfaces.ts';

type PositionerProps = ComponentProps<typeof BasePreviewCard.Positioner>;

export interface PreviewCardProps extends ComponentProps<typeof BasePreviewCard.Root> {
  /** The link being previewed; it becomes the trigger. */
  children: ReactElement;
  content: ReactNode;
  side?: PositionerProps['side'];
  align?: PositionerProps['align'];
  sideOffset?: PositionerProps['sideOffset'];
  arrow?: boolean;
  className?: string;
}

/**
 * The card that appears when a pointer rests on a link — a profile, an article,
 * a ticket. Hover-only by design: it must never hold anything a keyboard or
 * touch user cannot reach from the link itself.
 */
export function PreviewCard({
  children,
  content,
  side = 'bottom',
  align = 'center',
  sideOffset = 8,
  arrow = false,
  className,
  ...rootProps
}: PreviewCardProps) {
  return (
    <BasePreviewCard.Root {...rootProps}>
      <BasePreviewCard.Trigger render={children} />
      <BasePreviewCard.Portal>
        <BasePreviewCard.Positioner side={side} align={align} sideOffset={sideOffset}>
          <BasePreviewCard.Popup
            className={cx(
              popupSurface,
              popupTransition,
              'w-[min(22rem,calc(100vw-2rem))] p-4 text-sm',
              className,
            )}
          >
            {arrow ? (
              <BasePreviewCard.Arrow
                className={cx(
                  popupArrow,
                  'before:bg-surface-raised before:border before:border-border-subtle',
                )}
              />
            ) : null}
            {content}
          </BasePreviewCard.Popup>
        </BasePreviewCard.Positioner>
      </BasePreviewCard.Portal>
    </BasePreviewCard.Root>
  );
}
